import React, { useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { useToast } from "../../hooks/useToast";
import { IntegrationSecretsForm } from "../../forms/IntegrationSecretsForm";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { VStack, HStack } from "@chakra-ui/layout";
import { Skeleton, SkeletonText } from "@chakra-ui/react";

export const IntegrationSecrets = () => {
  const { businessId, integrationId } = useParams();
  const navigate = useNavigate();
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();

  const { data: integration, isLoading: integrationIsLoading } =
    useAdminApiRequest(
      (apiClient) => {
        if (!businessId || !integrationId) {
          return null;
        }
        return apiClient.getBusinessIntegration(businessId, integrationId);
      },
      [businessId, integrationId]
    );

  const {
    data: secretSpecs,
    isLoading: secretSpecsIsLoading,
    refresh,
  } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !integrationId) {
        return null;
      }
      return apiClient.getIntegrationSecret(businessId, integrationId);
    },
    [businessId, integrationId]
  );

  const isLoading = integrationIsLoading || secretSpecsIsLoading;

  const onSuccess = useCallback(() => {
    showSuccessToast("Integration secrets updated.");
    refresh();
  }, [showSuccessToast, refresh]);

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Integration secrets update failed.");
    },
    [showErrorToast]
  );

  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations`}
            >
              Integrations
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}`}
            >
              {integration?.IntegrationName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}/integration-secrets`}
            >
              Integration Secrets
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BusinessBreadcrumbs>
        <PageHeading.Title>Integration Secrets</PageHeading.Title>
      </PageHeading>
      <PageContent>
        {(isLoading && (
          <VStack
            w="100%"
            p={6}
            boxShadow="lg"
            alignItems="flex-start"
            spacing={4}
          >
            <Skeleton w="100px" height="40px" />
            <SkeletonText w="100%" mt="4" noOfLines={20} spacing="4" />
            <HStack w="100%">
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
            </HStack>
          </VStack>
        )) || (
          <IntegrationSecretsForm
            businessId={businessId!}
            integrationId={integration?.id!}
            secretSpecs={secretSpecs}
            apiClient={apiClient}
            onCancel={() => {
              navigate(
                `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`
              );
            }}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        )}
      </PageContent>
    </>
  );
};
